import { api } from '@/services/api'

import { reactive, shallowRef, type Ref } from 'vue'

import { useForm } from '@/services/form'
import { useToast } from '@/components/toast'
import { jobStatusHandlerMap, openLoadingModal } from './ping'
import type { JobStatus } from '=/types'
import { appendCanvasAnimated } from './animate'
import { setExampleImage } from './example'
import { closeInformation } from './components/information'

import type { AppType, Canvas, CanvasOptions, Information, RulesSet } from '=/types'

import overlay from '@/assets/images/overlays/terras.png'

const { showToast, removeToast } = useToast()

const controller = {
  render: new AbortController(),
  options: new AbortController(),
  download: new AbortController()
}

const loading = reactive({
  generate: 0
})

function showEmailConfirmation(values: Information, app: AppType) {
  const { showToast } = useToast()
  const message = app.lang?.toasts?.export?.replace('{email}', values.email) ?? `Texturen worden verzonden naar ${values.email}`
  showToast({
    message,
    type: 'success',
    duration: 4000
  })
}

export async function transcode(values: Information, app: AppType) {
  const { job } = await api.post<{ job: { id: string } }>(`export`, { body: values })

  if (values.method === 'email') {
    showEmailConfirmation(values, app)
    closeInformation()
    return
  }

  openLoadingModal()

  const checkStatus = async () => {
    const status = await api.get<JobStatus>(`status/${job.id}`)
    const handler = jobStatusHandlerMap[status.job.status]
    if (handler) handler(status, checkStatus)
  }

  checkStatus()
}

export async function useCanvas(element: Ref<HTMLCanvasElement | null>, initial: CanvasOptions, app: AppType) {
  const values = useForm<CanvasOptions>(initial)

  const rules = shallowRef<RulesSet>()

  async function options() {
    if (controller.options) {
      controller.options.abort()
    }

    controller.options = new AbortController()
    const { signal } = controller.options

    try {
      const data = await api.get<RulesSet>(`options`, { signal })
      rules.value = data
    } catch (error) {
      if (loading.generate) removeToast(loading.generate)
    }
  }

  async function render() {
    const loadingMessage = app.lang?.toasts?.loading ?? 'Aan het metselen...'
    const toastId = showToast({ message: loadingMessage, type: 'loading', duration: 3000 })
    loading.generate = toastId

    if (controller.render) {
      controller.render.abort('')
    }

    controller.render = new AbortController()
    const { signal } = controller.render

    try {
      if (!rules.value) await options()

      const data = await api.post<{ canvas: Canvas; filters: CanvasOptions; options: RulesSet }>(`canvas`, {
        body: values.form.value,
        signal
      })

      appendCanvasAnimated(element, data.canvas)
      setExampleImage(data.canvas, overlay, data.filters.sizes.height + data.filters.joint.spacing.y)
      Object.assign(values.form, data.filters)
      rules.value = data.options
    } catch (error) {
      removeToast(toastId)
    } finally {
      removeToast(toastId)
    }
  }

  return {
    values,
    rules,
    options,
    render,
    transcode
  }
}
